<template>
  <div
    class="m-checkbox no-focus-visible"
    v-bind="{
      tabindex: isNotFocus ? null : 0
    }"
    :class="classList"
    :style="checkboxStyle"
    @click="allowSelectByLabelClick ? containerClick($event) : null"
    @keydown.stop="$emit('handle-click-navigation', $event)"
  >
    <div
      class="m-checkbox__check-mark"
      @click="input(!value)"
    >
      <MIcon
        :icon="checkBoxIcon"
        class="m-checkbox__icon"
      />
    </div>
    <input
      :checked="value"
      type="checkbox"
      class="m-checkbox__hidden"
      @change="input($event.target.checked)"
    >
    <MIcon
      v-if="labelIcon"
      :icon="labelIcon"
      class="m-checkbox__icon"
      @click="input(!value)"
    />
    <MHint
      v-if="highlightInfo"
      :reference-selector="`#${id}`"
      :visible-arrow="isVisibleArrowOnHint"
      :show-on="showHintOn"
      :popper-class="popperClass"
      :popper-options="{modifiers: {
        preventOverflow: {
          enabled: true
        }
      }}"
    >
      <label
        :id="id"
        v-overflow-title="labelText"
        class="m-checkbox__label"
        @click="highlightInfo.isComplexHint ? null : input(!value)"
      >{{ labelText }}
      </label>
      <template
        v-if="!highlightInfo.isComplexHint"
        slot="content"
      >
        {{ highlightInfo.text }}
      </template>
      <template
        v-if="highlightInfo.isComplexHint"
        slot="content"
      >
        <slot name="complexHint" />
      </template>
    </MHint>
    <label
      v-else
      :id="id"
      v-overflow-title="labelText"
      class="m-checkbox__label"
      @click="allowSelectByLabelClick ? input(!value) : null"
    >{{ labelText }}
    </label>
  </div>
</template>

<script>
  import checkIcon from 'assets/icons/checkmark.svg';
  import iconIntermediate from 'assets/icons/check-intermediate.svg';
  import MIcon from 'components/Design/MIcon';
  import MHint from 'components/Help/MHint';

  const defaultBackgroundColor = '#FFFFFF';

  export default {
    name: 'MCheckbox',
    components: {MIcon, MHint},
    props: {
      value: {
        type: Boolean,
        default: false
      },
      labelText: {
        type: String,
        default: ''
      },
      disabled: {
        type: Boolean,
        default: false
      },
      isNotFocus: {
        type: Boolean,
        default: false
      },
      labelIcon: {
        type: Object,
        default: null
      },
      highlightInfo: {
        type: Object,
        default: null
      },
      id: {
        type: String,
        default: ''
      },
      intermediate: {
        type: Boolean,
        default: false
      },
      isVisibleArrowOnHint: {
        type: Boolean,
        default: true
      },
      isFocus: {
        type: Boolean,
        default: false
      },
      showHintOn: {
        type: String,
        default: 'hover'
      },
      popperClass: {
        type: String,
        default: null
      },
      allowSelectByLabelClick: {
        type: Boolean,
        default: true
      }
    },
    component: {MHint},
    computed: {
      classList () {
        return {
          'm-checkbox_disabled': this.disabled,
          'm-checkbox_checked': this.value && !this.intermediate,
          'm-checkbox_intermediate': this.intermediate,
          'select-option_focus': this.isFocus
        };
      },
      checkBoxIcon () {
        if (this.intermediate) {
          return iconIntermediate;
        }
        return checkIcon;
      },
      backgroundColor () {
        return this.highlightInfo ? this.highlightInfo.color : defaultBackgroundColor;
      },
      checkboxStyle () {
        if (this.highlightInfo) {
          return {
            backgroundColor: this.highlightInfo.color
          };
        }
        return null;
      }
    },
    watch: {
      isFocus () {
        if (this.isFocus) {
          this.$el.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
      }
    },
    methods: {
      input (value) {
        if (this.disabled) {
          return;
        }
        this.$emit('input', value);
      },
      containerClick (event) {
        if (event.currentTarget === event.target) {
          this.input(!this.value);
        }
      }
    }
  };

</script>

<style>
  .m-checkbox__label {
    /* deal with application.css */
    font-family: var(--main-font-family);
    font-size: var(--main-font-size);
    font-weight: var(--main-font-weight);

    color: var(--text-color);
    line-height: unset;

    /* ellipsis settings */
    text-overflow: ellipsis;

    white-space: nowrap;

    overflow: hidden;

    cursor: pointer;

    padding: 0;

    margin: 0;

    user-select: none;
  }

  .m-checkbox__hidden {
    display: none;
  }

  .m-checkbox__icon {
    flex-shrink: 0;

    width: 12px;
    height: 12px;

    margin-right: 8px;
  }

  .m-checkbox {
    /* constants. Change from the outside */
    --empty-color: var(--background-color);
    --default-color: var(--border-color);
    --checked-color: var(--action-color);
    --disabled-color: var(--gray-color);
    --disabled-checked: var(--action-disabled-color);
    --disabled-text-color: var(--gray-text-color);

    /* variables. Shouldn't be changed outside */
    --checkmark-border-color: var(--gray-text-color);
    --checkmark-background-color: var(--empty-color);
    --checkmark-color: var(--checkmark-background-color);
    --label-text-color: var(--text-color);

    cursor: pointer;

    display: flex;

    align-items: center;

    &.select-option_focus,
    &:not(.m-checkbox_checked):not(.m-checkbox_disabled):hover {
      --checkmark-border-color: var(--checked-color);
    }

    &.m-checkbox_disabled {
      --checkmark-background-color: var(--disabled-color);
      --checkmark-border-color: var(--disabled-color);
      --text-color: var(--disabled-text-color);

      cursor: not-allowed;

      & .m-checkbox__label {
        /* deal with application.css */
        cursor: not-allowed;
      }
    }

    &.m-checkbox_checked {
      --checkmark-background-color: var(--checked-color);
      --checkmark-border-color: var(--checked-color);
      --checkmark-color: var(--empty-color);
    }

    &.m-checkbox_disabled.m-checkbox_checked {
      --checkmark-background-color: var(--disabled-checked);
      --checkmark-border-color: var(--disabled-checked);
    }

    & .m-checkbox__check-mark {
      display: inline-flex;

      flex-shrink: 0;

      box-sizing: border-box;
      width: 14px;
      height: 14px;

      margin-right: 8px;

      background-color: var(--checkmark-background-color);

      border: 1px solid var(--checkmark-border-color);

      border-radius: 2px;

      user-select: none;

      & .m-checkbox__icon {
        color: var(--checkmark-color);

        width: 100%;
        height: 100%;
      }
    }
  }
</style>

